import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './UploadCompareProposals.module.css';
import Footer from '../../../components/common/Footer/Footer';

const UploadCompareProposals = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(null);

  const handleGetStarted = () => {
    const isAuthenticated = localStorage.getItem('token');
    if (isAuthenticated) {
      navigate('/homeowner/dashboard');
    } else {
      navigate('/homeowner/login');
    }
  };

  const handleCreateComparison = () => {
    const isAuthenticated = localStorage.getItem('token');
    if (isAuthenticated) {
      // Logged-in users go to dashboard with modal trigger
      navigate('/homeowner/dashboard?openModal=true');
    } else {
      // Guest users get free comparison
      navigate('/homeowner/create-comparison');
    }
  };

  const features = [
    {
      id: 'upload',
      icon: 'fas fa-cloud-upload-alt',
      title: 'Upload Multiple Proposals',
      description: 'Easily upload contractor proposals in PDF, DOC, or image formats. Our system handles multiple file types and organizes them automatically.',
      benefits: [
        'Support for PDF, Word, Excel, and image files',
        'Drag & drop interface for easy uploading',
        'Automatic file organization by contractor',
        'Secure cloud storage with data encryption'
      ]
    },
    {
      id: 'analyze',
      icon: 'fas fa-brain',
      title: 'AI-Powered Analysis',
      description: 'Our advanced AI analyzes each proposal against your project requirements, extracting key information and identifying important details.',
      benefits: [
        'Automatic cost breakdown extraction',
        'Timeline and schedule analysis',
        'Risk assessment & gap analysis',
        'Multi-dimensional contractor scoring'
      ]
    },
    {
      id: 'compare',
      icon: 'fas fa-balance-scale',
      title: 'Smart Comparison',
      description: 'Compare proposals side-by-side with detailed metrics, scoring, and recommendations to help you make the best decision.',
      benefits: [
        'Side-by-side proposal comparison',
        'Automated scoring and ranking',
        'Cost comparison analysis',
        'Risk assessment for each contractor'
      ]
    },
    {
      id: 'insights',
      icon: 'fas fa-chart-line',
      title: 'Detailed Insights',
      description: 'Get comprehensive insights including contractor ratings, market analysis, and personalized recommendations.',
      benefits: [
        'Quality vs. cost analysis',
        'AI-powered recommendations',
        'Behavioral analysis integration',
        'Risk assessment with severity scoring'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Upload Your Project Details',
      description: 'Start by uploading your project requirements, scope of work, and any existing documentation.',
      icon: 'fas fa-file-upload'
    },
    {
      step: '02',
      title: 'Add Contractor Proposals',
      description: 'Upload proposals from different contractors. Our system accepts multiple file formats.',
      icon: 'fas fa-users'
    },
    {
      step: '03',
      title: 'AI Analysis Process',
      description: 'Our AI analyzes each proposal, extracting key information and identifying important details.',
      icon: 'fas fa-cogs'
    },
    {
      step: '04',
      title: 'Compare & Decide',
      description: 'Review detailed comparisons, scores, and recommendations to make an informed decision.',
      icon: 'fas fa-trophy'
    }
  ];

  const useCases = [
    {
      title: 'Home Renovation Projects',
      description: 'Compare proposals for kitchen remodels, bathroom renovations, or whole-house updates.',
      icon: 'fas fa-home',
      examples: ['Kitchen Remodeling', 'Bathroom Renovation', 'Flooring Installation', 'Painting Projects']
    },
    {
      title: 'Construction Projects',
      description: 'Evaluate contractors for new construction, additions, or major structural work.',
      icon: 'fas fa-hammer',
      examples: ['Home Additions', 'Deck Construction', 'Garage Building', 'Foundation Work']
    },
    {
      title: 'Maintenance & Repairs',
      description: 'Compare quotes for ongoing maintenance, repairs, and improvement projects.',
      icon: 'fas fa-tools',
      examples: ['Roof Repairs', 'HVAC Installation', 'Plumbing Work', 'Electrical Updates']
    },
    {
      title: 'Landscaping Projects',
      description: 'Evaluate landscaping and outdoor improvement proposals from different contractors.',
      icon: 'fas fa-leaf',
      examples: ['Garden Design', 'Hardscaping', 'Irrigation Systems', 'Outdoor Lighting']
    }
  ];

  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroShapes}>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
          </div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1>Upload & Compare Proposals</h1>
            <p className={styles.heroSubtitle}>
              Transform your contractor selection process with AI-powered proposal analysis and comparison
            </p>
            <p className={styles.heroDescription}>
              Upload multiple contractor proposals and let our advanced AI analyze, compare, and rank them 
              based on cost, quality, timeline, and your specific project requirements.
            </p>
            <div className={styles.heroButtons}>
              <button className={styles.primaryButton} onClick={handleCreateComparison}>
                <i className="fas fa-rocket"></i>
                Start Comparison
              </button>
              <button className={styles.secondaryButton} onClick={handleGetStarted}>
                <i className="fas fa-play"></i>
                View Dashboard
              </button>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.dashboardPreview}>
              <div className={styles.previewHeader}>
                <div className={styles.previewTabs}>
                  <div className={styles.previewTab}>Overview</div>
                  <div className={styles.previewTab}>Analysis</div>
                  <div className={styles.previewTab}>Compare</div>
                </div>
              </div>
              <div className={styles.previewContent}>
                <div className={styles.analysisSummary}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryValue}>3</span>
                    <span className={styles.summaryLabel}>Proposals Analyzed</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryValue}>12</span>
                    <span className={styles.summaryLabel}>Risks Identified</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryValue}>5</span>
                    <span className={styles.summaryLabel}>Missing Items Found</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryValue}>8.5/10</span>
                    <span className={styles.summaryLabel}>Best Score</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.processSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>How It Works</h2>
            <p>Our streamlined process makes contractor comparison simple and efficient</p>
          </div>
          <div className={styles.processGrid}>
            {processSteps.map((step, index) => (
              <div key={index} className={styles.processStep}>
                <div className={styles.stepNumber}>{step.step}</div>
                <div className={styles.stepIcon}>
                  <i className={step.icon}></i>
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className={styles.stepConnector}>
                    <i className="fas fa-arrow-right"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Powerful Features</h2>
            <p>Everything you need to make informed contractor decisions</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={feature.id} 
                className={`${styles.featureCard} ${activeFeature === feature.id ? styles.active : ''}`}
                onMouseEnter={() => setActiveFeature(feature.id)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className={styles.featureIcon}>
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className={styles.featureBenefits}>
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className={styles.benefit}>
                      <i className="fas fa-check"></i>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={styles.useCasesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Perfect For Any Project</h2>
            <p>From small repairs to major renovations, we've got you covered</p>
          </div>
          <div className={styles.useCasesGrid}>
            {useCases.map((useCase, index) => (
              <div key={index} className={styles.useCaseCard}>
                <div className={styles.useCaseIcon}>
                  <i className={useCase.icon}></i>
                </div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
                <div className={styles.examples}>
                  {useCase.examples.map((example, idx) => (
                    <span key={idx} className={styles.exampleTag}>
                      {example}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsContent}>
            <div className={styles.benefitsText}>
              <h2>Why Choose Our Platform?</h2>
              <div className={styles.benefitsList}>
                <div className={styles.benefitItem}>
                  <i className="fas fa-clock"></i>
                  <div>
                    <h4>Save Time</h4>
                    <p>Reduce proposal review time from days to hours with automated analysis</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-dollar-sign"></i>
                  <div>
                    <h4>Save Money</h4>
                    <p>Make better decisions and avoid costly mistakes with data-driven insights</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-shield-alt"></i>
                  <div>
                    <h4>Reduce Risk</h4>
                    <p>Comprehensive contractor analysis and risk assessment</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-chart-line"></i>
                  <div>
                    <h4>Better Outcomes</h4>
                    <p>Choose the right contractor based on objective analysis and scoring</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.benefitsVisual}>
              <div className={styles.statsCard}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>10+</span>
                  <span className={styles.statLabel}>Evaluation Dimensions</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>5-10 Min</span>
                  <span className={styles.statLabel}>Analysis Time</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>Multi-Format</span>
                  <span className={styles.statLabel}>Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2>Ready to Transform Your Contractor Selection?</h2>
            <p>Join thousands of homeowners who have streamlined their renovation process</p>
            <div className={styles.ctaButtons}>
              <button className={styles.primaryButton} onClick={handleCreateComparison}>
                <i className="fas fa-rocket"></i>
                Start Your First Comparison
              </button>
              <button className={styles.secondaryButton} onClick={() => navigate('/homeowner/pricing')}>
                <i className="fas fa-tag"></i>
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UploadCompareProposals; 